import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Link,
  Card,
  CardContent,
  CardMedia,
  useTheme,
  Skeleton,
  Chip,
} from '@mui/material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

// Inline SVG fallback
const PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800">
    <defs><linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0%" stop-color="#e5e7eb"/><stop offset="100%" stop-color="#cbd5e1"/></linearGradient></defs>
    <rect width="1200" height="800" fill="url(#g)"/>
    <g fill="#475569" font-family="Arial, Helvetica, sans-serif" text-anchor="middle">
      <text x="600" y="390" font-size="28" font-weight="700">Property image</text>
      <text x="600" y="425" font-size="14" opacity="0.85">Not available</text>
    </g>
  </svg>`);

function formatCurrencyGBP(value, text) {
  if (text) return text;
  if (value == null) return 'POA';
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(value);
  } catch {
    return `£${Number(value).toLocaleString('en-GB')}`;
  }
}

function fmtAuction(d) {
  // Output as DD/MM/YY to match your example
  if (!d) return '';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return d; // already a string like 16/08/25
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

export default function FeaturedPropertiesSection() {
  const theme = useTheme();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/properties.php?featured=1&status=active&limit=12`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json.ok) throw new Error(json.error || 'Failed to load properties');
        if (!ignore) setProperties(Array.isArray(json.data) ? json.data : []);
      } catch (e) {
        if (!ignore) setErr(e.message || 'Network error');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  const sliderSettings = useMemo(() => ({
    dots: false,
    infinite: true,
    speed: 600,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: true,
    responsive: [
      { breakpoint: 1200, settings: { slidesToShow: 2 } },
      { breakpoint: 768,  settings: { slidesToShow: 1 } },
    ],
  }), []);

  return (
    <Box component="section" sx={{ bgcolor: theme.palette.grey[100], py: { xs: 6, md: 10 } }}>
      <Container maxWidth="lg">
        {/* Heading + link */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 4, md: 6 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            Featured
          </Typography>
          <Link
            href="/properties?types=sale"
            underline="none"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              color: theme.palette.error.main,
              fontWeight: 600,
              position: 'relative',
              pl: 3.5,
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                width: 24,
                height: 2,
                bgcolor: 'error.main',
              },
            }}
          >
            View all properties for sale
          </Link>
        </Box>

        {err && (
          <Typography color="error" sx={{ mb: 2, fontWeight: 700 }}>
            {err}
          </Typography>
        )}

        {/* Loading skeletons */}
        {loading && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Skeleton variant="rectangular" height={200} />
                <Box sx={{ p: 2 }}>
                  <Skeleton width="80%" />
                  <Skeleton width="60%" />
                  <Skeleton width="40%" />
                </Box>
              </Card>
            ))}
          </Box>
        )}

        {/* Carousel */}
        {!loading && properties.length > 0 && (
          <Slider {...sliderSettings}>
            {properties.map((prop) => {
              const img = prop.full_image_url || prop.image_url || PLACEHOLDER;
              const url = prop.href || (prop.slug ? `/property/${prop.slug}` : `#`);
              const isAuction = (prop.listing_type || '').toLowerCase() === 'auction' || !!prop.auction_date;
              const priceLabel = isAuction ? 'Guide Price' : (prop.listing_type === 'rent' ? 'Rent' : 'Price');
              const priceDisplay = formatCurrencyGBP(prop.price, prop.price_text);

              return (
                <Box key={prop.id} px={1}>
                  <Card
                    sx={{
                      position: 'relative',
                      boxShadow: 1,
                      borderRadius: 2,
                      m: 1,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {/* Image */}
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        image={img}
                        alt={prop.title}
                        sx={{ height: 200, objectFit: 'cover', display: 'block' }}
                        onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER) e.currentTarget.src = PLACEHOLDER; }}
                        loading="lazy"
                      />

                      {/* Listing type pill */}
                      {prop.listing_type && (
                        <Chip
                          size="small"
                          label={String(prop.listing_type).toUpperCase()}
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: 'rgba(0,0,0,.6)',
                            color: '#fff',
                            fontWeight: 700,
                          }}
                        />
                      )}

                      {/* Auction date badge */}
                      {isAuction && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 16,
                            left: 16,
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            bgcolor: 'common.black',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 1,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: 'common.white', textAlign: 'center', lineHeight: 1.15, fontSize: 11 }}>
                            Auction<br />{fmtAuction(prop.auction_date)}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Body */}
                    <CardContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Typography
                          component="a"
                          href={url}
                          variant="subtitle1"
                          sx={{
                            color: theme.palette.primary.dark,
                            fontWeight: 600,
                            flex: '1 1 auto',
                            pr: 1,
                            textDecoration: 'none',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: 48, // keeps rows even
                          }}
                        >
                          {prop.title}
                        </Typography>

                        <Box sx={{ textAlign: 'right', minWidth: 120 }}>
                          <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
                            {priceLabel}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ color: 'error.main', fontWeight: 700 }}>
                            {priceDisplay}
                          </Typography>
                        </Box>
                      </Box>

                      <Typography variant="body2" color="text.secondary">
                        {(prop.beds ?? 0)} bedroom{(prop.beds ?? 0) > 1 ? 's' : ''}&nbsp;·&nbsp;
                        {(prop.baths ?? 0)} bathroom{(prop.baths ?? 0) > 1 ? 's' : ''}{prop.town ? ` · ${prop.town}` : ''}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              );
            })}
          </Slider>
        )}
      </Container>
    </Box>
  );
}
